import { useCallback, useEffect, useState } from "react";
import { Send, AlertTriangle, CheckCircle2, MessageSquareWarning, Loader2 } from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SUBMISSION_STATUS } from "@/lib/strings";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TARGET_LABELS = { QUESTION: "Domanda", DATA: "Dato", DOCUMENT: "Documento", OTHER: "Richiesta" };

// Barra "Invia al consulente" + richieste di integrazione (Fase 6F).
// L'azienda vede SOLO stato invio e richieste a lei indirizzate: mai note o analisi del consulente.
export default function SubmissionBar({ refreshToken = 0 }) {
  const { companyId, period, can } = useTenant();
  const [sub, setSub] = useState(null);
  const [requests, setRequests] = useState([]);
  const [warnings, setWarnings] = useState(null);
  const [blocking, setBlocking] = useState(null);
  const [busy, setBusy] = useState(false);
  const canWrite = can("esg:write");

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const [{ data: s }, { data: r }] = await Promise.all([
        api.get(`/companies/${companyId}/periods/${period.id}/submission`),
        api.get(`/companies/${companyId}/periods/${period.id}/integration-requests`),
      ]);
      setSub(s);
      setRequests(r);
    } catch {
      setSub(null);
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load, refreshToken]);

  const submit = async (confirm) => {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/submission/submit`,
        { confirm_warnings: confirm });
      toast.success("Compilazione inviata al consulente");
      setWarnings(null);
      setBlocking(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "ESG_INCONSISTENT_DATA") {
        setWarnings(null);
        setBlocking(detail);
      } else if (e?.response?.status === 409) {
        setBlocking(null);
        setWarnings(detail?.warnings || {});
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const review = async (action) => {
    let bankName;
    if (action === "SEND_TO_BANK") {
      bankName = window.prompt("Nome della banca destinataria della Scheda di sostenibilità:");
      if (!bankName || !bankName.trim()) return;
    }
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/submission/review`,
        { action, bank_name: bankName?.trim() });
      toast.success(action === "APPROVE" ? "Compilazione approvata" : "Segnata come inviata alla banca");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (req) => {
    setBusy(true);
    try {
      await api.post(
        `/companies/${companyId}/periods/${period.id}/integration-requests/${req.id}/resolve`);
      toast.success("Richiesta segnata come evasa");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!sub) return null;
  const effective = sub.effective_status || sub.submission_status;
  const status = SUBMISSION_STATUS[effective] || SUBMISSION_STATUS.DRAFT;
  const isResubmission = effective === "READY_FOR_RESUBMISSION";
  const hasBlocking = (sub.blocking_inconsistencies ?? 0) > 0;
  const canSubmit = canWrite && !hasBlocking
    && (["DRAFT", "RETURNED_FOR_CORRECTION"].includes(effective) || isResubmission);
  const openReqs = requests.filter((r) => r.status === "OPEN");

  return (
    <div className="space-y-3" data-testid="submission-bar">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-600">Invio al consulente:</span>
          <Badge className={status.cls} data-testid="submission-status-badge">{status.label}</Badge>
          {hasBlocking && (
            <span className="text-xs font-semibold text-red-700" data-testid="submission-blocked-note">
              bloccato · {sub.blocking_inconsistencies} incongruenze bloccanti
            </span>
          )}
          {sub.submitted_at && (
            <span className="text-xs text-slate-400">
              il {new Date(sub.submitted_at).toLocaleDateString("it-IT")}
            </span>
          )}
        </div>
        {canSubmit && (
          <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
            disabled={busy} onClick={() => submit(false)} data-testid="submit-to-consultant-button">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" aria-hidden="true" />
              : <Send className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}
            {isResubmission ? "Invia nuovamente al consulente" : "Invia al consulente"}
          </Button>
        )}
        {can("submission:review") && effective === "ACCEPTED_FOR_ANALYSIS" && !hasBlocking && (
          <Button size="sm" className="h-8 text-xs bg-emerald-700 hover:bg-emerald-800 text-white"
            disabled={busy} onClick={() => review("APPROVE")} data-testid="approve-button">
            Approva
          </Button>
        )}
        {effective === "APPROVED_BY_CONSULTANT" && (can("submission:review") || canWrite) && (
          <Button size="sm" className="h-8 text-xs bg-indigo-700 hover:bg-indigo-800 text-white"
            disabled={busy} onClick={() => review("SEND_TO_BANK")} data-testid="send-to-bank-button">
            <Send className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Inviato alla banca
          </Button>
        )}
        {effective === "SENT_TO_BANK" && sub.bank_name && (
          <span className="text-xs text-indigo-800" data-testid="sent-to-bank-info">Banca: {sub.bank_name}</span>
        )}
      </div>

      {warnings && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="submit-warnings">
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" aria-hidden="true" />
            Prima dell'invio, verifica questi punti
          </p>
          <ul className="text-sm text-amber-800 mt-2 space-y-1 list-disc list-inside">
            <li data-testid="warning-missing">Informazioni mancanti: <strong>{warnings.missing}</strong></li>
            <li data-testid="warning-toverify">Dati da verificare: <strong>{warnings.to_verify}</strong></li>
            <li data-testid="warning-documents">Documenti caricati: <strong>{warnings.documents_count}</strong></li>
          </ul>
          <div className="flex gap-2 mt-3">
            <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              disabled={busy} onClick={() => submit(true)} data-testid="submit-confirm-button">
              Invia comunque al consulente
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs"
              onClick={() => setWarnings(null)} data-testid="submit-cancel-button">
              Continua la compilazione
            </Button>
          </div>
        </div>
      )}

      {blocking && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4" data-testid="submit-blocking">
          <p className="text-sm font-semibold text-red-900 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" aria-hidden="true" />
            Invio bloccato: {blocking.blocking_count} dat{blocking.blocking_count === 1 ? "o incoerente" : "i incoerenti"} da correggere
          </p>
          <ul className="text-sm text-red-800 mt-2 space-y-1.5">
            {(blocking.issues || []).map((i) => (
              <li key={i.rule_code} className="flex flex-wrap items-center gap-2" data-testid={`blocking-issue-${i.rule_code}`}>
                <span className="font-mono text-[11px] text-red-600">{i.datapoint_code}</span>
                <span>{i.message}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-red-700 mt-3">
            Gli errori bloccanti non sono aggirabili: correggi i dati nel questionario e riprova l'invio.
          </p>
          <Button size="sm" variant="outline" className="h-8 text-xs mt-3 border-red-300 text-red-800"
            onClick={() => setBlocking(null)} data-testid="submit-blocking-dismiss">
            Torna alla compilazione
          </Button>
        </div>
      )}

      {openReqs.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2" data-testid="integration-requests-banner">
          <p className="text-sm font-semibold text-red-900 flex items-center gap-1.5">
            <MessageSquareWarning className="w-4 h-4" aria-hidden="true" />
            Il consulente ha richiesto {openReqs.length} integrazion{openReqs.length === 1 ? "e" : "i"}
          </p>
          <ul className="space-y-2">
            {openReqs.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 rounded-md bg-white border border-red-100 px-3 py-2"
                data-testid={`integration-request-${r.id}`}>
                <Badge variant="outline" className="text-xs">{TARGET_LABELS[r.target_type] || r.target_type}</Badge>
                {r.target_code && <span className="text-xs font-mono text-slate-500">{r.target_code}</span>}
                <span className="text-sm text-slate-800 flex-1 min-w-0">{r.comment}</span>
                {canWrite && (
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                    disabled={busy} onClick={() => resolve(r)}
                    data-testid={`integration-resolve-${r.id}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Evadi
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
